import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Star, Trash2, Plus } from "lucide-react";

const EMPTY = { name: "", context: "", rating: 5, text: "", branch: "", review_date: new Date().toISOString().slice(0, 10), visible: true };

export const ReviewsManager = () => {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: k === "rating" ? Number(e.target.value) : e.target.value });

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/reviews", form);
      toast.success("Review added");
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (r) => {
    try {
      const { review_id, source, created_at, ...body } = r;
      await api.put(`/admin/reviews/${r.review_id}`, { ...body, visible: !r.visible });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Review removed");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-5" data-testid="reviews-manager">
      <div className="bg-white rounded-lg border border-[#e2ebe4] p-5">
        <h2 className="heading-upper text-sm tracking-widest text-[#144733] mb-1">Google reviews manager</h2>
        <p className="text-xs text-[#57685e] mb-4">Paste real Google reviews here — only 4★ and above appear on the home page carousel. For a live nightly sync we'd need a Google Places API key and branch Place IDs.</p>
        <form onSubmit={add} className="grid sm:grid-cols-2 gap-3">
          <input className="field-input" placeholder="Reviewer name (e.g. Ayesha K.)" required value={form.name} onChange={set("name")} data-testid="review-name-input" />
          <input className="field-input" placeholder="Context (e.g. Tourist, Dubai Marina · Weekly rental)" required value={form.context} onChange={set("context")} data-testid="review-context-input" />
          <input className="field-input" placeholder="Branch" required value={form.branch} onChange={set("branch")} data-testid="review-branch-input" />
          <div className="flex gap-3">
            <select className="field-input" value={form.rating} onChange={set("rating")} data-testid="review-rating-select">
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} stars</option>)}
            </select>
            <input type="date" className="field-input" value={form.review_date} onChange={set("review_date")} data-testid="review-date-input" />
          </div>
          <textarea className="field-input sm:col-span-2 min-h-[70px]" placeholder="Review text" required value={form.text} onChange={set("text")} data-testid="review-text-input" />
          <button type="submit" disabled={busy} className="btn-cta rounded px-5 py-2.5 text-xs flex items-center gap-2 w-fit disabled:opacity-50" data-testid="review-add-btn"><Plus className="w-4 h-4" /> Add review</button>
        </form>
      </div>
      <div className="bg-white rounded-lg border border-[#e2ebe4] divide-y divide-[#f1f3f1]" data-testid="reviews-list">
        {reviews.map((r) => (
          <div key={r.review_id} className="p-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#0F1E17]">
                {r.name}
                <span className="flex items-center gap-0.5 text-[#fbbc04] text-xs"><Star className="w-3 h-3 fill-[#fbbc04]" />{r.rating}</span>
                <span className="text-[10px] text-[#8a978f]">{r.review_date} · {r.branch}</span>
              </div>
              <p className="text-xs text-[#57685e] mt-1 line-clamp-2">{r.text}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => toggle(r)} className={`text-[10px] font-bold px-2 py-1 rounded-full ${r.visible ? "bg-[#f0f9f0] text-[#009900]" : "bg-gray-100 text-gray-500"}`} data-testid={`review-toggle-${r.review_id}`}>
                {r.visible ? "Visible" : "Hidden"}
              </button>
              <button onClick={() => remove(r.review_id)} className="text-red-500 p-1" data-testid={`review-delete-${r.review_id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
